import { createTheme } from "@mui/material";
import { COLORS, font, fontSize } from "./tokens";

const headingFont = `"${font.heading}", sans-serif`; // Oswald
const bodyFont = `"${font.body}", system-ui, -apple-system, sans-serif`; // Inter

const heading = {
  fontFamily: headingFont,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "-0.02em",
  lineHeight: 1.1
} as const;

const theme = createTheme({
  cssVariables: true,

  colorSchemes: {
    light: {
      palette: {
        primary: {
          main: COLORS.black,
          light: COLORS.blackT1,
          dark: COLORS.black,
          contrastText: COLORS.white
        },
        secondary: {
          main: COLORS.red,
          light: COLORS.redT1,
          dark: COLORS.redT2,
          contrastText: COLORS.white
        },
        error: { main: COLORS.tomato },
        warning: { main: COLORS.orange },
        info: { main: COLORS.blue },
        success: { main: COLORS.green },
        background: { default: COLORS.white, paper: COLORS.white },
        text: {
          primary: COLORS.black,
          secondary: COLORS.blackT2,
          disabled: COLORS.blackT4
        },
        divider: COLORS.blackT4
      }
    }
  },

  shape: { borderRadius: 4 },
  spacing: 8,
  typography: {
    fontFamily: bodyFont,

    h1: { ...heading, fontSize: fontSize.xxl },
    h2: { ...heading, fontSize: fontSize.xl },
    h3: { ...heading, fontSize: fontSize.lg },
    h4: { ...heading, fontWeight: 600, fontSize: fontSize.md },
    h5: { ...heading, fontWeight: 600, fontSize: fontSize.sm },
    h6: { ...heading, fontWeight: 600, fontSize: fontSize.xs },

    subtitle1: {
      fontFamily: headingFont,
      fontWeight: 500,
      fontSize: fontSize.xs,
      textTransform: "uppercase",
      letterSpacing: "0.08em"
    },
    subtitle2: {
      fontFamily: headingFont,
      fontWeight: 500,
      fontSize: fontSize.xxs,
      textTransform: "uppercase",
      letterSpacing: "0.08em"
    },

    body1: { fontWeight: 400, fontSize: fontSize.sm, lineHeight: 1.5 },
    body2: { fontWeight: 400, fontSize: fontSize.xs, lineHeight: 1.5 },

    button: {
      fontWeight: 600,
      fontSize: fontSize.xs,
      textTransform: "uppercase",
      letterSpacing: "0.05em"
    },

    caption: { fontWeight: 400, fontSize: fontSize.xxs },

    overline: {
      fontFamily: headingFont,
      fontWeight: 500,
      fontSize: fontSize.xxs,
      textTransform: "uppercase",
      letterSpacing: "0.1em"
    }
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 750, // based on Router.jsx's media query
      lg: 900,
      xl: 1200
    }
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em"
        },
        outlined: { borderWidth: 2 },
        contained: { border: "2px solid transparent" } // keeps sizing consistent with outlined
      }
    },

    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderBottom: `2px solid ${theme.palette.text.primary}`
        })
      }
    },

    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRight: `2px solid ${theme.palette.text.primary}`
        })
      }
    },

    MuiPaper: {
      styleOverrides: {
        outlined: ({ theme }) => ({
          borderWidth: 2,
          borderColor: theme.palette.text.primary
        })
      }
    },

    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: `2px solid ${theme.palette.text.primary}`,
          borderRadius: 4
        })
      }
    },

    MuiTabs: {
      styleOverrides: {
        indicator: ({ theme }) => ({
          height: 2,
          backgroundColor: theme.palette.text.primary
        })
      }
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "uppercase",
          fontWeight: 600,
          letterSpacing: "0.05em"
        }
      }
    },

    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: { borderWidth: 2 }
      }
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 4 },
        outlined: { borderWidth: 2 }
      }
    }
  }
});

export default theme;
